import type { ReactElement } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { MetricEntry, RangePreset } from '../../api/metricsTypes.js';
import { filterByRange, formatChartTick } from './bmiUtils.js';

const PRESETS: readonly RangePreset[] = ['1M', '3M', '6M', 'All'];

export interface MetricsChartProps {
  entries: MetricEntry[];
  range: RangePreset;
  onRangeChange: (r: RangePreset) => void;
}

export function MetricsChart({
  entries,
  range,
  onRangeChange,
}: MetricsChartProps): ReactElement {
  const data = filterByRange(entries, range);

  const cardStyle = {
    background: 'var(--bg-surface)',
    borderColor: 'var(--border)',
    color: 'var(--text-main)',
  } as const;

  return (
    <section
      aria-label="Weight timeline chart"
      data-testid="metrics-chart-card"
      className="rounded-2xl border p-5 shadow-sm"
      style={cardStyle}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-lg font-semibold">Weight timeline</span>
        <div
          className="flex gap-1"
          role="tablist"
          aria-label="Time range"
          data-testid="metrics-range-pills"
        >
          {PRESETS.map((p) => {
            const active = p === range;
            return (
              <button
                key={p}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={`metrics-range-${p}`}
                data-active={active ? 'true' : 'false'}
                onClick={() => onRangeChange(p)}
                className="rounded-full border px-3 py-1 text-xs font-medium"
                style={{
                  minHeight: 32,
                  background: active ? 'var(--accent)' : 'transparent',
                  color: active ? 'var(--accent-contrast)' : 'var(--text-main)',
                  borderColor: 'var(--border)',
                }}
              >
                {p}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-4" data-testid="metrics-chart-area">
        {data.length === 0 ? (
          <p
            className="py-10 text-center text-sm"
            style={{ color: 'var(--text-muted)' }}
            data-testid="metrics-chart-empty"
          >
            No data in this range
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <LineChart
              data={data.map((e) => ({
                measured_at: e.measured_at,
                weight_kg: e.weight_kg,
              }))}
              margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
            >
              <CartesianGrid
                stroke="var(--border)"
                strokeDasharray="3 3"
              />
              <XAxis
                dataKey="measured_at"
                tickFormatter={formatChartTick}
                stroke="var(--text-muted)"
                tick={{ fill: 'var(--text-muted)', fontSize: 12 }}
              />
              <YAxis
                dataKey="weight_kg"
                stroke="var(--text-muted)"
                tick={{ fill: 'var(--text-muted)', fontSize: 12 }}
                domain={['auto', 'auto']}
                unit=" kg"
              />
              <Tooltip
                contentStyle={{
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border)',
                  color: 'var(--text-main)',
                }}
                labelFormatter={(label) => formatChartTick(String(label ?? ''))}
                formatter={(value) => {
                  const n = typeof value === 'number' ? value : Number(value);
                  return [
                    `${Number.isFinite(n) ? n.toFixed(1) : String(value)} kg`,
                    'Weight',
                  ];
                }}
              />
              <Line
                type="monotone"
                dataKey="weight_kg"
                stroke="var(--accent)"
                dot={false}
                activeDot={{ r: 5 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </section>
  );
}
